import dayjs from "dayjs";

import { useMemo } from "react";

import { FlightWayEnum, RoundTripTypeEnum } from "@/constants";
import { AIRLINE_NAME_MAP } from "@/constants/airline";
import { AIRPORT_MAP } from "@/constants/airport";
import { STOP_NUMBER_MAP } from "@/constants/filters";
import { BANK_NAME_MAP, PROGRAM_NAME_MAP } from "@/constants/programs";
import { TimePoints } from "@/constants/timeRange";
import useSearchQuery from "@/hooks/useSearchQuery";
import { UnknownAirlineKey } from "@/pages/Routes/components/FilterBar/utils/constants";

import { FilterOptions, FilterSourceData, OptionItem } from "../typing";
import { AircraftUnknown, MixedAirlineKey } from "../utils/constants";

const CabinsOptions = ["Economy", "Premium Economy", "Business", "First"];

const transformerOptions = (map: Map<string, string>): OptionItem[] => {
  const options = [];
  for (const [value, label] of map.entries()) {
    options.push({ label, value });
  }

  return options;
};

const useProgramsOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const programsOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const programCode = list[index].program_code;
      programsOptionsMap.set(programCode, PROGRAM_NAME_MAP.get(programCode) || "");
    }
    return transformerOptions(programsOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useAirlinesOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const airlinesOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const array = list[index].fare.products;
      for (let j = 0; j < array.length; j++) {
        const airlineCode = array[j].airline_code;

        if (airlineCode) {
          airlinesOptionsMap.set(airlineCode, airlineCode);
        } else {
          airlinesOptionsMap.set(UnknownAirlineKey, UnknownAirlineKey);
        }
      }
    }
    return transformerOptions(airlinesOptionsMap).sort((a, b) =>
      AIRLINE_NAME_MAP[a.label]?.localeCompare(AIRLINE_NAME_MAP[b.label])
    );
  }, [list]);
};

const useAircraftsOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const aircraftsOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      list[index].fare.products.forEach(({ aircraft }) => {
        aircraft = aircraft || AircraftUnknown;
        aircraftsOptionsMap.set(aircraft, aircraft);
      });
    }
    return transformerOptions(aircraftsOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useAirportOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const airportOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      list[index].fare.products.forEach(({ origin, destination }) => {
        airportOptionsMap.set(origin, `${AIRPORT_MAP[origin]?.name}(${origin})`);
        airportOptionsMap.set(destination, `${AIRPORT_MAP[destination]?.name}(${destination})`);
      });
    }
    return transformerOptions(airportOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useAirportOutboundOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const airportOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const origin = list[index].fare.products[0].origin;
      airportOptionsMap.set(origin, `${AIRPORT_MAP[origin]?.name}(${origin})`);
    }
    return transformerOptions(airportOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useAirportInboundOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const airportOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const destination =
        list[index].fare.products[list[index].fare.products.length - 1].destination;
      airportOptionsMap.set(destination, `${AIRPORT_MAP[destination]?.name}(${destination})`);
    }
    return transformerOptions(airportOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useConnectingAirportsOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const airportOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      if (list[index].fare.products.length > 1) {
        const connectingAirports = list[index].fare.products.slice(
          1,
          list[index].fare.products.length - 1
        );

        const first_destination = list[index].fare.products[0].destination;
        airportOptionsMap.set(
          first_destination,
          `${AIRPORT_MAP[first_destination]?.name}(${first_destination})`
        );
        const last_origin = list[index].fare.products[list[index].fare.products.length - 1].origin;
        airportOptionsMap.set(last_origin, `${AIRPORT_MAP[last_origin]?.name}(${last_origin})`);

        connectingAirports.forEach((item) => {
          const destination = item.destination;
          airportOptionsMap.set(destination, `${AIRPORT_MAP[destination]?.name}(${destination})`);
          const origin = item.origin;
          airportOptionsMap.set(origin, `${AIRPORT_MAP[origin]?.name}(${origin})`);
        });
      }
    }
    return transformerOptions(airportOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useBanksOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const banksOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const transferOptions = list[index].transfer_options;
      if (!transferOptions) continue;
      transferOptions.forEach(({ program }) => {
        banksOptionsMap.set(program, BANK_NAME_MAP[program]);
      });
    }
    // console.log("transferOptions: ", banksOptionsMap);
    return transformerOptions(banksOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useMaxPointsRange = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list.map((item) => item.award_points).sort((a, b) => a - b);

    return [Number(data[0]), Number(data[data.length - 1])];
  }, [list]);
};

const useTaxRange = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list.map((item) => item.surcharge).sort((a, b) => a - b);

    return [Number(data[0]), Number(data[data.length - 1])];
  }, [list]);
};

const usePremiumRange = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list.map((item) => item.percent_premium).sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useDurationRange = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list.map((item) => item.fare.travel_minutes_total).sort((a, b) => a - b);

    return [Number(data[0]), Number(data[data.length - 1])];
  }, [list]);
};

const useConnectionTimeRange = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => {
        return item.fare.products.reduce((a, b) => a + b.layover_time, 0);
      })
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useDatesOptions = (
  list: FilterSourceData,
  roundTripType: RoundTripTypeEnum
): FilterOptions["datesOptions"] => {
  const { query } = useSearchQuery();
  return useMemo(() => {
    const aircraftsOptionsMap = new Map<string, { tax: number; awardPoints: number }>();

    if (query.range) {
      const dateRanges = [];

      if (query.flightWay === FlightWayEnum.OneWay) {
        dateRanges.push([query.oneWayRangeStartDate, query.oneWayRangeEndDate]);
      }

      if (query.flightWay === FlightWayEnum.RoundTrip) {
        if (roundTripType === RoundTripTypeEnum.Outbound) {
          // 去程
          dateRanges.push([query.departureRangeStartDate, query.departureRangeEndDate]);
        } else {
          // 回程
          dateRanges.push([query.returnRangeStartDate, query.returnRangeEndDate]);
        }
      }

      for (let i = 0; i < dateRanges.length; i++) {
        const dateRangeItem = dateRanges[i];
        if (!dateRangeItem.some((v) => !v)) {
          const diff = dayjs(dateRangeItem[1]).diff(dateRangeItem[0], "day") + 1;
          for (let i = 0; i < diff; i++) {
            const d = dayjs(dateRangeItem[0]).add(i, "day").format("YYYY-MM-DD");
            aircraftsOptionsMap.set(d, {
              awardPoints: 0,
              tax: 0,
            });
          }
        }
      }

      // for (let index = 0; index < list.length; index++) {
      //   const item = list[index];
      //   if (
      //     aircraftsOptionsMap.get(item.date) &&
      //     (!aircraftsOptionsMap.get(item.date)?.awardPoints ||
      //       // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      //       item.award_points < aircraftsOptionsMap.get(item.date)!.awardPoints)
      //   ) {
      //     aircraftsOptionsMap.set(item.date, {
      //       awardPoints: item.award_points,
      //       tax: item.surcharge,
      //     });
      //   }
      // }
    }

    return Array.from(aircraftsOptionsMap.entries()).map(([key, value]) => {
      return {
        value: key,
        label: key,
        ...value,
      };
    });
  }, [query, roundTripType]);
};

const useCabinsOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const cabinsOptionsMap = new Map<string, string>();

    for (let index = 0; index < list.length; index++) {
      const cabinType = list[index].payment?.cabin || list[index].final_cabin_type;
      cabinsOptionsMap.set(cabinType, cabinType);
    }

    const sortedOptions = transformerOptions(cabinsOptionsMap).sort((a, b) => {
      const order = ["Economy", "Premium Economy", "Business", "First"];
      return order.indexOf(a.label) - order.indexOf(b.label);
    });
    return sortedOptions;
  }, [list]);
};

const useMaxPointsRangeEconomy = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.cabin_prices?.Economy?.miles)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useMaxPointsRangePremiumEconomy = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.cabin_prices?.["Premium Economy"]?.miles)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useMaxPointsRangeFirst = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.cabin_prices?.First?.miles)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useMaxPointsRangeBusiness = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.cabin_prices?.Business?.miles)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useTaxRangeEconomy = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.cabin_prices?.Economy?.tax)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useTaxRangePremiumEconomy = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.cabin_prices?.["Premium Economy"]?.tax)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useTaxRangeFirst = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.cabin_prices?.First?.tax)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useTaxRangeBusiness = (list: FilterSourceData): [number, number] => {
  return useMemo(() => {
    const data = list
      .map((item) => item.cabin_prices?.Business?.tax)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return [data[0], data[data.length - 1]];
  }, [list]);
};

const useAdvisoryOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const advisoryMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const advisory = list[index].advisory ?? [];

      advisory.forEach((k) => {
        advisoryMap.set(k, k);
      });
    }
    return transformerOptions(advisoryMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};

const useDaysOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const daysMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const date = list[index].date;
      const day = dayjs(date).day();
      daysMap.set(day.toString(), dayjs(date).format("dddd"));
    }
    return transformerOptions(daysMap).sort((a, b) => a.value.localeCompare(b.value));
  }, [list]);
};

/**
 * 根据数据列表获取筛选项
 */
export const useFilterOptions = (
  list: FilterSourceData,
  roundTripType: RoundTripTypeEnum
): FilterOptions => {
  const programsOptions = useProgramsOptions(list);
  const airlinesOptions = useAirlinesOptions(list);
  const banksOptions = useBanksOptions(list);
  const airportOptions = useAirportOptions(list);
  const airportInboundOptions = useAirportInboundOptions(list);
  const airportOutboundOptions = useAirportOutboundOptions(list);
  const connectingAirportsOptions = useConnectingAirportsOptions(list);
  const maxPointsRange = useMaxPointsRange(list);
  const taxRange = useTaxRange(list);
  const premiumRange = usePremiumRange(list);
  const durationRange = useDurationRange(list);
  const connectionTimeRange = useConnectionTimeRange(list);
  const aircraftsOptions = useAircraftsOptions(list);
  const datesOptions = useDatesOptions(list, roundTripType);
  const cabinsOptions = useCabinsOptions(list);

  const maxPointsRangeEconomy = useMaxPointsRangeEconomy(list);
  const maxPointsRangePremiumEconomy = useMaxPointsRangePremiumEconomy(list);
  const maxPointsRangeFirst = useMaxPointsRangeFirst(list);
  const maxPointsRangeBusiness = useMaxPointsRangeBusiness(list);

  const taxRangeEconomy = useTaxRangeEconomy(list);
  const taxRangePremiumEconomy = useTaxRangePremiumEconomy(list);
  const taxRangeBusiness = useTaxRangeBusiness(list);
  const taxRangeFirst = useTaxRangeFirst(list);

  const advisoryOptions = useAdvisoryOptions(list);
  const daysOptions = useDaysOptions(list);

  return {
    programsOptions,
    airlinesOptions: airlinesOptions,
    stopsOptions: transformerOptions(STOP_NUMBER_MAP),
    banksOptions,
    airportOptions,
    airportInboundOptions,
    airportOutboundOptions,
    maxPointsRange,
    taxRange,
    premiumRange,
    durationRange,
    connectionTimeRange,
    aircraftsOptions,
    commonTimeRange: TimePoints,
    datesOptions,
    connectingAirportsOptions,
    cabinsOptions: cabinsOptions,
    condition: {},

    maxPointsRangeEconomy,
    maxPointsRangePremiumEconomy,
    maxPointsRangeFirst,
    maxPointsRangeBusiness,

    taxRangeEconomy,
    taxRangePremiumEconomy,
    taxRangeBusiness,
    taxRangeFirst,

    gridModeCabinsOptions: CabinsOptions.map((item) => ({ value: item, label: item })),

    advisoryOptions,
    daysOptions,
  };
};
